import { useMemo } from 'react';
import { useParams } from 'react-router';
import { usePlan } from './usePlan';

const rankings = [
    ['cat1', 'beauty'],
    ['cat1', 'personal care'],
    ['cat1', 'wellness'],
    ['cat1', 'grocery'],
    ['cat2', 'fresh & perishable foods'],
    ['cat1', 'beverages'],
    ['cat1', 'ingredients'],
    ['cat1', 'home & pet'],
    ['cat1', 'cultural shifts'],
    ['cat1', 'occasions'],
    ['cat2', 'dishes'],
    ['cat2', 'cuisines'],
    ['cat2', 'diets & meals'],
];

const getCanonicalHierarchy = (hierarchies) => {
    if (hierarchies.length < 2) return hierarchies[0];
    for (const [depth, name] of rankings) {
        const current = hierarchies.filter(
            ([_parent, hierarchy_hint]) => hierarchy_hint[depth] === name,
        );
        if (current.length === 1) return current[0];
    }
    return hierarchies[0];
};

export const getHierarchy = (hierarchies, parentId) => {
    if (!(hierarchies && hierarchies.length)) return {};
    let hierarchy;
    if (parentId)
        hierarchy = hierarchies.find(
            ([currentParentId]) => currentParentId === parentId,
        );
    if (!hierarchy) hierarchy = getCanonicalHierarchy(hierarchies);
    const [parent, hierarchy_hint] = hierarchy;
    return { parent, hierarchy_hint };
};

export const getParentIdByAncestorHint = (hierarchies, hint) => {
    if (!(hint && hierarchies)) return;
    const filteredHierarchies = hierarchies.filter(
        ([_parent, hierarchy_hint]) =>
            Object.entries(hint).every(
                ([key, value]) => hierarchy_hint[key] === value,
            ),
    );
    const { parent } = getHierarchy(filteredHierarchies);
    return parent;
};

const getHierarchyByParentId = (parentId) =>
    function (hierarchies) {
        return getHierarchy(hierarchies, parentId);
    };

const getHierarchyByUserPlan = (plan) => (hierarchies) => {
    const userHierarchies = hierarchies?.filter(([_parent, hierarchy]) =>
        plan?.industry?.includes(hierarchy.cat1),
    );
    if (userHierarchies?.length) return getHierarchy(userHierarchies);
    return getHierarchy(hierarchies);
};

/**
 * Memoised because consumers put it in dependency arrays: the factories below
 * return a fresh closure on every call, which made every `useMemo` keyed on it
 * -- the row merge in `useGoogleData`, once per render at ~30 call sites --
 * recompute unconditionally.
 */
const useGetHierarchy = () => {
    const { plan } = usePlan();
    const params = useParams();
    const parentId = params?.parentId;
    const industry = plan?.industry;

    return useMemo(() => {
        if (parentId) return getHierarchyByParentId(parentId);
        if (industry) return getHierarchyByUserPlan({ industry });
        return getHierarchy;
    }, [parentId, industry]);
};

export default useGetHierarchy;
